import EmojiReactions from '@/components/ui/emojis/emoji-reactions.tsx';
import useLocalStorage from '@/hooks/use-local-storage';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Trade } from '@/models/registry';
import {
  CommentsOrderBy,
  EmojiReactionType,
  type ResellType,
} from '@/models/types';
import dayjs from 'dayjs';
import { ArrowDown01, ArrowDown10, CircleUser } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useMemo, type ReactNode } from 'react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import ActivityFeed from './activity-feed/activity-feed';
import TradeLocationEditor from './location/trade-location-editor';
import { TradePayoffDisplay } from './payoff/trade-payoff-display';
import PrimaryCustomerNameEditor from './primary-customer/primary-customer-name-editor';
import TradeAssignedTasks from './tasks/trade-assigned-tasks';
import TradeTaskSelector from './tasks/trade-task-selector';
import TradeUploadButton from './uploads/trade-upload-button';
import TradeVehicleEditor from './vehicle/trade-vehicle-editor';

const Row = ({ children }: { children: ReactNode }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
  );
};

const Colummn = ({
  children,
  align,
}: {
  children: ReactNode;
  align: 'start' | 'end';
}) => {
  return (
    <div
      className={cn('flex flex-row', {
        'justify-start': align === 'start',
        'lg:justify-end': align === 'end',
      })}
    >
      <div className="flex flex-col gap-2 w-full min-w-56 max-w-96">
        {children}
      </div>
    </div>
  );
};

const NonMobileMain = observer(({ trade }: { trade: Trade }) => {
  console.log('non-mobile-main trade', trade);

  const [orderBy, setOrderBy] = useLocalStorage<CommentsOrderBy>(
    'deal-activity-feed-order',
    CommentsOrderBy.Newest,
  );

  const auditLogs = useMemo(() => {
    // DEV NOTE: in order reuse the `ActivityFeed` component, we to pass in a `auditLogs` prop
    // We're doing this until we support the audits for the trade in appraisal
    return trade.comments
      .map((comment) => {
        return {
          id: comment.id,
          type: 'comment' as const,
          deal_id: trade.deal_id || '',
          deal: null,
          created_at: comment.created_at,
        };
      })
      .sort((a, b) => {
        return dayjs(a.created_at).diff(dayjs(b.created_at));
      });
  }, [JSON.stringify(trade.comments)]);

  // TODO: TECH DEBT: make this interface better
  // const { deal } = useDeal(tradeInAppraisal.deal_id || '');
  // const deal = dealStore.getById(tradeInAppraisal.deal_id || '');

  return (
    // TODO: tie the h-calc on this to the header height
    <div
      className="flex flex-col gap-2 p-4 h-[calc(100vh-40px)] overflow-auto w-full dark:bg-dark"
      // ref={scrollContainerRef}
      // onScroll={onScroll}
    >
      <Row>
        <Colummn align="start">
          <div className="flex flex-col gap-1">
            <PrimaryCustomerNameEditor
              id={trade.id}
              fullName={trade.full_name}
              shortId={trade.short_id}
            />
          </div>
        </Colummn>
      </Row>

      <div className="w-full border-t"></div>

      <Row>
        <Colummn align="start">
          <h5 className="scroll-m-20 text-lg  tracking-tight w-full">
            Vehicle
          </h5>
          <TradeVehicleEditor
            tradeId={trade.id}
            vin={trade.vin}
            make={trade.make}
            model={trade.model}
            trim={trade.trim}
            year={trade.year}
            mileage={trade.mileage}
            condition={trade.condition}
            numberOfKeys={trade.number_of_keys}
            exteriorColor={trade.exterior_color}
            interiorColor={trade.interior_color}
            titlePresent={trade.title_present}
            lienholder={trade.lienholder || ''}
            payoffAmount={trade.payoff_amount || 0}
            appraisedValue={trade.appraised_value || 0}
            resellType={trade.resell_type as ResellType}
          />
        </Colummn>

        <Colummn align="end">
          <h5 className="scroll-m-20 text-lg  tracking-tight w-full">
            Locations
          </h5>

          <TradeLocationEditor
            tradeId={trade.id}
            currentLocation={trade.current_location}
            everLocation={trade.ever_location || ''}
            state={trade.state || ''}
            postalCode={trade.postal_code || ''}
          />

          <TradePayoffDisplay trade={trade} />
        </Colummn>
      </Row>

      <div className="w-full border-t"></div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-2 items-center flex-wrap justify-between p-2">
          <EmojiReactions
            targetId={trade.id}
            targetType={EmojiReactionType.Trade}
          />
          <TradeUploadButton />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <TradeTaskSelector dealId={trade.id} withHokeyListener />
          <TradeAssignedTasks />
        </div>
      </div>

      <div className="w-full border-t"></div>

      {/* Activity Feed */}
      {/* TODO: TECH DEBT: consolidate this with the deal page */}

      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center justify-between">
          <h6 className="scroll-m-20 text-md  tracking-tight">Activity</h6>
          <div className="flex flex-row gap-1 items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  {orderBy === CommentsOrderBy.Newest ? (
                    <ArrowDown10
                      className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                      onClick={() => {
                        setOrderBy(CommentsOrderBy.Oldest);
                      }}
                    />
                  ) : (
                    <ArrowDown01
                      className={cn('h-3.5 w-3.5 cursor-pointer', {})}
                      onClick={() => {
                        setOrderBy(CommentsOrderBy.Newest);
                      }}
                    />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {orderBy === CommentsOrderBy.Newest
                  ? 'Newest activity first'
                  : 'Oldest activity first'}
              </TooltipContent>
            </Tooltip>
            <div className="flex flex-row gap-1 items-center">
              <Button
                variant={'ghost'}
                className="h-fit p-2"
                onClick={() => {
                  toast({
                    title: 'Feature not implemented yet',
                    description:
                      "Slack us for the 'unsubscribe from deal notifications' feature",
                  });
                }}
              >
                <div className="text-xs  text-gray-500 text-nowrap">
                  Unsubscribe
                </div>
              </Button>

              <CircleUser
                className={cn('h-3.5 w-3.5 cursor-pointer text-gray-500', {})}
              />
            </div>
          </div>
        </div>

        <div className="pb-16">
          <ActivityFeed
            auditLogs={auditLogs}
            audits={[]}
            comments={trade.comments || []}
            orderBy={orderBy}
            tradeId={trade.id}
            tradeShortId={trade.short_id}
          />
        </div>
      </div>

      {/* {!deal && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col items-start justify-start gap-1">
            <h6 className="scroll-m-20 text-md  tracking-tight">
              Activity
            </h6>
            <div className="text-sm text-muted-foreground">
              Activity Feed and Comments are available only when a deal is
              associated with this trade in appraisal.
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
});

export default NonMobileMain;
